import { CargoContent } from 'src/types/ViewModel/Stowage/Manifest/CargoContent';
import { areCargoCommingleable } from './cargoUtils';

/**
 * Identifies non-commingleable cargo pairs within a collection of cargo contents
 * based on their operations, sequence, and cargo properties.
 *
 * The following cases are supported:
 * 1. Commingleable cargoes in same berth call, loading before unloading
 *    - Returns empty array
 *
 * 2. Non-commingleable cargoes in same berth call, loading before unloading
 *    - Returns both cargoes
 *
 * 3. Commingleable cargoes in same berth call, unloading before loading
 *    - Returns empty array
 *
 * 4. Non-commingleable cargoes in same berth call, unloading before loading
 *    - Returns empty array
 *
 * 5. Non-commingleable cargoes in same berth call, unloading with weight > 0 before loading
 *    - Returns both cargoes
 *
 * 6. Transit cargo (no operation) with non-commingleable cargo with loading operation
 *    - Returns both cargoes
 *
 * 7. Transit cargo (no operation) with non-commingleable cargo with unloading operation
 *    - Returns both cargoes (transit cargo always has weight > 0)
 *
 * @param cargoContents - Array of cargo contents to check for non-commingleable pairs
 * @returns Array of non-commingleable cargo contents
 */
export const getNonCommingleableCargoes = (cargoContents: CargoContent[]): CargoContent[] => {
    if (cargoContents.length < 2) return [];
    const nonCommingleableCargoes: Set<CargoContent> = new Set();

    for (let i = 0; i < cargoContents.length; i++) {
        const cargoContent1 = cargoContents[i];

        for (let j = 0; j < cargoContents.length; j++) {
            if (i === j) continue;
            const cargoContent2 = cargoContents[j];

            // Handle transit cargo scenarios (cargo without operations OR with Transit operation type)
            if (
                !cargoContent1.cargoOperation ||
                !cargoContent2.cargoOperation ||
                cargoContent1.cargoOperation?.operationType === 'Transit' ||
                cargoContent2.cargoOperation?.operationType === 'Transit'
            ) {
                const commingleable = areCargoCommingleable(cargoContent1.cargo, cargoContent2.cargo);

                if (!commingleable) {
                    nonCommingleableCargoes.add(cargoContent1);
                    nonCommingleableCargoes.add(cargoContent2);
                }
                continue;
            }

            // Skip if not in sequence order (we only care about operations in sequence)
            if (cargoContent1.cargoOperation.cargoOperationSequenceNumber >= cargoContent2.cargoOperation.cargoOperationSequenceNumber) {
                continue;
            }

            const commingleable = areCargoCommingleable(cargoContent1.cargo, cargoContent2.cargo);

            if (!commingleable) {
                // Case 2: Loading before Unloading with non-commingleable cargoes
                if (cargoContent1.cargoOperation.operationType === 'Loading' && cargoContent2.cargoOperation.operationType === 'Unloading') {
                    nonCommingleableCargoes.add(cargoContent1);
                    nonCommingleableCargoes.add(cargoContent2);
                }

                // Case 5: Unloading (with weight > 0) before Loading with non-commingleable cargoes
                if (
                    cargoContent1.cargoOperation.operationType === 'Unloading' &&
                    cargoContent2.cargoOperation.operationType === 'Loading' &&
                    cargoContent1.weight.greaterThan(0)
                ) {
                    nonCommingleableCargoes.add(cargoContent1);
                    nonCommingleableCargoes.add(cargoContent2);
                }
            }
        }
    }

    return Array.from(nonCommingleableCargoes);
};
